import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { PRIVE_PROMISE, COLLECTIONS, DESTINATIONS } from "../data/content";

export default function Prive() {
  return (
    <main data-testid="prive-page" className="bg-navy">
      {/* Hero */}
      <section className="relative h-[100svh] overflow-hidden">
        <img src={DESTINATIONS[0].hero} alt="Privé bespoke luxury travel" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.62)" }} />
        <div className="relative h-full max-w-[1500px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[14vh]">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }} className="overline text-gold mb-6">Privé · Bespoke Luxury Travel</motion.p>
          <h1 className="font-serif text-ivory text-6xl md:text-8xl leading-[0.95] max-w-[15ch]">
            <MaskLines lines={["Reserved for you.", "Remembered forever."]} delay={0.3} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 1 }} className="text-ivory/70 max-w-xl mt-8 text-lg leading-relaxed">
            Bespoke journeys crafted with discretion, elegance and exceptional attention to detail. From private island escapes to once-in-a-lifetime expeditions, every experience is curated exclusively for you.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 1 }} className="mt-10">
            <Link to="/enquire?type=prive" data-testid="prive-hero-cta" className="btn-gold">Begin Your Bespoke Journey <ArrowRight size={16} /></Link>
          </motion.div>
        </div>
      </section>

      {/* Promise */}
      <section className="bg-ivory py-28 md:py-40" data-testid="prive-promise">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="overline text-charcoal/50">The Privé Promise</p>
            <h2 className="font-serif text-4xl md:text-6xl text-navy mt-6 max-w-[22ch] leading-[1.05]">Luxury is defined by thoughtful curation, not extravagance alone.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-navy/10 mt-16">
            {PRIVE_PROMISE.map((p, idx) => (
              <Reveal key={p.t} delay={(idx % 3) * 0.06} className="bg-ivory">
                <div className="p-8 md:p-10 h-full">
                  <p className="font-serif text-gold text-2xl">0{idx + 1}</p>
                  <h3 className="font-serif text-2xl text-navy mt-5">{p.t}</h3>
                  <p className="text-charcoal/60 mt-3 leading-relaxed text-[15px]">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Collections */}
      <section className="bg-navy grain py-28 md:py-40" data-testid="prive-collections">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10">
          <Reveal className="mb-16">
            <p className="overline text-gold">Luxury Collections</p>
            <h2 className="font-serif text-4xl md:text-6xl text-ivory mt-5">Curated worlds to explore.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {COLLECTIONS.map((c, idx) => (
              <Reveal key={c.name} delay={(idx % 3) * 0.07}>
                <Link to={`/destinations/${c.slug}`} className="group block relative overflow-hidden h-[420px] grain" data-testid={`prive-collection-${c.name.toLowerCase().replace(/\s|&/g, "")}`}>
                  <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(13,16,56,0.85), transparent 60%)" }} />
                  <div className="absolute bottom-0 p-8">
                    <h3 className="font-serif text-4xl text-ivory">{c.name}</h3>
                    <p className="text-ivory/70 mt-3 text-sm max-w-xs leading-relaxed">{c.sub}</p>
                    <span className="inline-flex items-center gap-2 text-gold overline mt-5">Discover <ArrowUpRight size={14} /></span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32 md:py-48 overflow-hidden" data-testid="prive-cta">
        <img src={DESTINATIONS[4].hero} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover animate-kenburns" />
        <div className="absolute inset-0" style={{ background: "rgba(13,16,56,0.72)" }} />
        <div className="relative max-w-[1500px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <h2 className="font-serif text-ivory text-5xl md:text-7xl max-w-[20ch] mx-auto leading-[1.05]">Your next extraordinary journey begins here.</h2>
            <p className="text-ivory/70 max-w-xl mx-auto mt-8 leading-relaxed">Our Privé Curators are ready to craft a journey designed exclusively for you.</p>
            <Link to="/enquire?type=prive" className="btn-gold mt-12" data-testid="prive-bottom-cta">Request a Bespoke Itinerary <ArrowRight size={16} /></Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
